// Számla-DETAIL (full-page, READ-ONLY belső nézet). Az `invoiceDetail` (InvoiceDetailSchema) MINDEN
// megjeleníthető mezőjét mutatja: fejléc (számlaszám / „Piszkozat" + típus-/státusz-badge + akciók) +
// bizonylat-meta (pénznem/kézbesítés/fizetési mód + dátumok) + Kibocsátó és Vevő egymás mellett +
// tétel-táblázat + összesítő + fej-/lábléc-megjegyzés + érintett fiókok.
//
// A LAP NYELVE: FORM-/DETAIL-oldal, mint az ügyfél-, fiók-, profil- és jegy-oldal — `FormPage`
// (BALRA igazított, keskeny hasáb, saját görgetéssel) + `FormSection`-ök: NINCS kártya-keret és külön háttér,
// a tagolást a szekció-cím (overline) és a térköz adja. Korábban `PageBody` + `card-glass-lg` blokkok voltak
// (a LISTA-oldalak nyelve), és a lap tetején külön „vissza" gomb ült — a navigációt a topbar-morzsa
// (`AppShell parent`) adja, a vissza-gomb csak a hiba-/tiltás-állapotban kell. A pénz-mezők a backend Decimal→string-jei (SOHA nem float-olunk — csak formázzuk). Page-gate:
// `invoices.view`. Draft + `invoices.manage` → „Szerkesztés" gomb az edit-route-ra; nem-draft immutábilis
// (a kiállítás/storno/PDF a Számlázz.hu-integrációs fázis — itt NINCS ilyen akció).
//
// A KIBOCSÁTÓ és a SZÁMLÁZOTT FIÓKOK a DETAIL-válaszból jönnek: a kibocsátó a számlán TÁROLT snapshotból
// (név/adószám/cím/bank — a kiállításkor lemásolva), a fiókok pedig névvel feloldva. Ez két dolgot ad, amit a
// korábbi megoldás (kliens-oldali merge a `invoiceRefs` picker-végpontból) nem: (1) HITELESSÉG — a számla jogi
// bizonylat, tehát a kibocsátó AKKORI adatait kell mutatnia, nem a törzs mai állapotát; (2) a nézet `view`
// joggal is TELJES — a picker-végpont `invoices.manage`-gated, ezért korábban csak-view joggal a kibocsátó a
// kulcs-címkéjére, a fiókok darabszámra degradáltak.
import { type InvoiceDetailSchema } from '@atlas/contracts';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowUturnLeftIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { InvoiceStatusBadge } from '@/components/invoices/invoice-status-badge';
import { InvoiceTypeBadge } from '@/components/invoices/invoice-type-badge';
import { AppShell } from '@/components/shared/app-shell';
import { EmptyState } from '@/components/shared/empty-state';
import { FormPage } from '@/components/shared/form-page';
import { FormSection } from '@/components/shared/form-section';
import { Button } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { invoiceKeys } from '@/features/invoices/api';
import {
  InvoiceActionDialogs,
  useInvoiceActions,
} from '@/features/invoices/components/invoice-action-dialogs';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { formatDay } from '@/lib/format-datetime';
import { formatMoney } from '@/lib/format-money';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

export const Route = createFileRoute('/invoices/$id/')({
  beforeLoad: () => requireAuth(),
  component: InvoiceDetailPage,
});

type InvoiceDetail = z.infer<typeof InvoiceDetailSchema>;

/** Egy címke→érték sor; üres érték → nem renderel (a tickets-detail mintája). */
function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element | null {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="min-w-0 space-y-1">
      <Text variant="overline" as="div">
        {label}
      </Text>
      <div className="text-sm">{children}</div>
    </div>
  );
}

/** Van-e bármilyen cím-adat (a Field-kapuhoz — a JSX-elem maga mindig truthy lenne). */
function hasAddressParts(...parts: (string | null)[]): boolean {
  return parts.some((s) => s !== null && s.trim() !== '');
}

/** Cím-blokk: irányítószám + település egy sorban, utca külön, ország halványan. */
function AddressBlock({
  zip,
  city,
  address,
  country,
}: {
  zip: string | null;
  city: string | null;
  address: string | null;
  country: string | null;
}): JSX.Element {
  const cityLine = [zip, city].filter((s) => s !== null && s.trim() !== '').join(' ');
  return (
    <div className="space-y-0.5">
      {cityLine && <div>{cityLine}</div>}
      {address && <div>{address}</div>}
      {country && <div className="text-muted-foreground">{country}</div>}
    </div>
  );
}

export function InvoiceDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('invoices.view');
  const canManage = can('invoices.manage');

  // Számla-kiállítási életciklus (Számlázz.hu): a hook MINDIG (early-return előtt) hívódik (hook-szabály).
  const actions = useInvoiceActions(t);

  const gated = !permsLoading && !canView;

  const detailKey = invoiceKeys.detail(id);
  const detailQuery = api.invoiceDetail.useQuery(
    detailKey,
    { params: { id } },
    { queryKey: detailKey },
  );
  const detail: InvoiceDetail | undefined =
    detailQuery.data?.status === 200 ? detailQuery.data.body : undefined;
  const errStatus = detailQuery.error?.status;
  const isForbidden = gated || isForbiddenStatus(errStatus);

  const emDash = <span className="text-muted-foreground">—</span>;
  const backButton = (
    <Button variant="outline" onClick={() => void navigate({ to: '/invoices' })}>
      <ArrowLeftIcon className="h-4 w-4" />
      {t('invoices.detail.back')}
    </Button>
  );

  if (!gated && detailQuery.isLoading) {
    return (
      <AppShell>
        <FormPage tight>
          <div className="space-y-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </FormPage>
      </AppShell>
    );
  }

  if (isForbidden || !detail) {
    return (
      <AppShell parent={{ label: t('nav.invoices'), to: '/invoices' }}>
        <FormPage tight>
          <EmptyState
            title={isForbidden ? t('invoices.forbidden') : t('invoices.notFound')}
            description={isForbidden ? t('invoices.forbiddenDesc') : t('invoices.notFoundDesc')}
            action={backButton}
          />
        </FormPage>
      </AppShell>
    );
  }

  const isDraft = detail.status === 'draft';
  // A PISZKOZAT ideiglenes száma (`TMP-INV-…`) MEGJELENIK (a legacy is így tett) — a placeholder csak a régi,
  // technikai `DRAFT-{uuid}` sorokra jut (lásd a lista-oldal ugyanazon döntését).
  const isDraftNumber =
    detail.invoiceNumber === null ||
    detail.invoiceNumber === '' ||
    detail.invoiceNumber.startsWith('DRAFT-');
  const displayNumber = isDraftNumber ? t('invoices.draftNumberPlaceholder') : detail.invoiceNumber;

  // Kiállítási életciklus-akciók elérhetősége (státusz + típus + jog szerint).
  const isActive = detail.status === 'issued' || detail.status === 'overdue';
  const isProforma = detail.type === 'proforma';
  const isStorno = detail.status === 'storno';
  const isDownloadable = ['issued', 'paid', 'overdue', 'storno'].includes(detail.status);
  const actionRow = {
    id: detail.id,
    version: detail.version,
    status: detail.status,
    type: detail.type,
    invoiceNumber: detail.invoiceNumber,
    stornoInvoiceNumber: detail.stornoInvoiceNumber,
    // A kiállítás-ablak ezekből számolja a kiállítás napjára igazított dátum-előnézetet.
    issueDate: detail.issueDate,
    fulfillmentDate: detail.fulfillmentDate,
    dueDate: detail.dueDate,
  };

  // Kibocsátó: a törzs-snapshot (refs) neve, vagy fallback a kulcs-címkére. A kulcs sosem null a UI-on (none).
  const issuerKey = detail.issuerKey ?? 'none';
  // A KIBOCSÁTÓ a számla SNAPSHOT-mezőiből (a kiállításkor lemásolt törzs-adat), nem a törzs MAI állapotából:
  // a számla jogi bizonylat — azt kell mutatnia, ami rajta van. Fallback a kulcs-címke (régi, snapshot
  // nélküli sor). Ez egyben a `view`-jogú felhasználót is kiszolgálja (a picker-végpont manage-gated).
  const issuerName =
    detail.issuerName ?? t(`invoices.issuerLabel.${issuerKey}`, { defaultValue: issuerKey });
  const hasIssuerSnapshot =
    !!detail.issuerTaxNumber ||
    !!detail.issuerAddress ||
    !!detail.issuerCity ||
    !!detail.issuerBankAccount;

  // Érintett fiókok: a szerver NÉVVEL adja (client-scope pivot vagy account-scope egyetlen fiók).
  const billedIds =
    detail.accountIds.length > 0 ? detail.accountIds : detail.accountId ? [detail.accountId] : [];
  const resolvedAccountNames = detail.billedAccounts.map((a) => a.name);

  const dueClass = detail.status === 'overdue' ? 'text-danger' : undefined;

  const hasNote = !!detail.note;

  return (
    <AppShell
      parent={{ label: t('nav.invoices'), to: '/invoices' }}
      title={displayNumber ?? undefined}
    >
      <FormPage tight>
        <div className="space-y-8">
          {/* ══ FEJLÉC ══ a számlaszám (piszkozatnál „Piszkozat") a főcím, alatta EGY meta-sor a típus- és
              státusz-badge-dzsel; a bizonylat-életciklus akciói a cím MELLETT, jobbra. A „vissza" gomb
              kivezetve: a navigációt a topbar-morzsa adja (`AppShell parent`). */}
          <div className="space-y-2.5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h1 className="text-2xl font-semibold">
                {isDraftNumber ? (
                  <span className="text-muted-foreground italic">{displayNumber}</span>
                ) : (
                  (displayNumber ?? emDash)
                )}
              </h1>
              <div className="flex flex-wrap items-center gap-2">
                {isDraft && canManage && (
                  <Button
                    variant="outline"
                    onClick={() => void navigate({ to: '/invoices/$id/edit', params: { id } })}
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    {t('invoices.edit')}
                  </Button>
                )}
                {isDraft && canManage && (
                  <Button variant="brand" onClick={() => actions.openIssue(actionRow)}>
                    <PaperAirplaneIcon className="h-4 w-4" />
                    {t('invoices.actions.issue')}
                  </Button>
                )}
                {/* PDF-letöltés: sztornónál a sztornó- ÉS az eredeti PDF is; egyébként az alap. */}
                {isStorno && detail.stornoInvoiceNumber && (
                  <Button
                    variant="outline"
                    disabled={actions.pdfPendingId === detail.id}
                    onClick={() => void actions.downloadPdf(detail.id, 'storno')}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    {t('invoices.actions.pdfStorno')}
                  </Button>
                )}
                {isDownloadable && detail.invoiceNumber && (
                  <Button
                    variant="outline"
                    disabled={actions.pdfPendingId === detail.id}
                    onClick={() => void actions.downloadPdf(detail.id, 'invoice')}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    {isStorno ? t('invoices.actions.pdfOriginal') : t('invoices.actions.pdf')}
                  </Button>
                )}
                {isActive && canManage && (
                  <Button variant="outline" onClick={() => actions.openStorno(actionRow)}>
                    <ArrowUturnLeftIcon className="h-4 w-4" />
                    {t('invoices.actions.storno')}
                  </Button>
                )}
                {isActive && isProforma && canManage && (
                  <Button variant="outline" onClick={() => actions.openCancel(actionRow)}>
                    <NoSymbolIcon className="h-4 w-4" />
                    {t('invoices.actions.cancel')}
                  </Button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {detail.type && (
                <InvoiceTypeBadge
                  type={detail.type}
                  deliveryType={detail.deliveryType}
                  status={detail.status}
                  t={t}
                />
              )}
              <InvoiceStatusBadge
                status={detail.status}
                label={t(`invoices.statusLabel.${detail.status}`, {
                  defaultValue: detail.status,
                })}
              />
            </div>
          </div>

          {/* Bizonylat-meta: pénznem / kézbesítés / fizetési mód + dátumok */}
          <FormSection title={t('invoices.detail.sections.document')}>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              <Field label={t('invoices.detail.fields.currency')}>{detail.currency}</Field>
              <Field label={t('invoices.detail.fields.deliveryType')}>
                {detail.deliveryType
                  ? t(`invoices.deliveryTypeLabel.${detail.deliveryType}`, {
                      defaultValue: detail.deliveryType,
                    })
                  : emDash}
              </Field>
              <Field label={t('invoices.detail.fields.paymentMethod')}>
                {detail.paymentMethod
                  ? t(`invoices.paymentMethodLabel.${detail.paymentMethod}`, {
                      defaultValue: detail.paymentMethod,
                    })
                  : emDash}
              </Field>
              <Field label={t('invoices.detail.fields.fulfillmentDate')}>
                {formatDay(detail.fulfillmentDate, locale) || emDash}
              </Field>
              <Field label={t('invoices.detail.fields.issueDate')}>
                {formatDay(detail.issueDate, locale) || emDash}
              </Field>
              <Field label={t('invoices.detail.fields.dueDate')}>
                {detail.dueDate ? (
                  <span className={dueClass}>{formatDay(detail.dueDate, locale)}</span>
                ) : (
                  emDash
                )}
              </Field>
              {detail.paymentDate && (
                <Field label={t('invoices.detail.fields.paymentDate')}>
                  {formatDay(detail.paymentDate, locale)}
                </Field>
              )}
              {detail.stornoInvoiceNumber && (
                <Field label={t('invoices.detail.fields.stornoInvoiceNumber')}>
                  {detail.stornoInvoiceNumber}
                </Field>
              )}
              {detail.relatedInvoiceNumber && (
                <Field label={t('invoices.detail.fields.relatedInvoiceNumber')}>
                  {detail.relatedInvoiceNumber}
                </Field>
              )}
              {detail.stornoComment && (
                <Field label={t('invoices.detail.fields.stornoComment')}>
                  <span className="whitespace-pre-line">{detail.stornoComment}</span>
                </Field>
              )}
            </div>
          </FormSection>

          {/* Kibocsátó + Vevő egymás mellett (keret nélkül, a szekció-cím tagol) */}
          <div className="grid gap-8 md:grid-cols-2">
            <FormSection title={t('invoices.detail.sections.issuer')}>
              <div className="space-y-4">
                <Field label={t('invoices.detail.fields.name')}>{issuerName}</Field>
                {hasIssuerSnapshot && (
                  <>
                    <Field label={t('invoices.detail.fields.taxNumber')}>
                      {detail.issuerTaxNumber}
                    </Field>
                    <Field label={t('invoices.detail.fields.euTaxNumber')}>
                      {detail.issuerEuTaxNumber}
                    </Field>
                    {hasAddressParts(
                      detail.issuerZipCode,
                      detail.issuerCity,
                      detail.issuerAddress,
                      detail.issuerCountryCode,
                    ) && (
                      <Field label={t('invoices.detail.fields.address')}>
                        <AddressBlock
                          zip={detail.issuerZipCode}
                          city={detail.issuerCity}
                          address={detail.issuerAddress}
                          country={detail.issuerCountryCode}
                        />
                      </Field>
                    )}
                    {(!!detail.issuerBankName || !!detail.issuerBankAccount) && (
                      <Field label={t('invoices.detail.fields.bank')}>
                        <div className="space-y-0.5">
                          {detail.issuerBankName && <div>{detail.issuerBankName}</div>}
                          {detail.issuerBankAccount && (
                            <div className="tabular-nums">{detail.issuerBankAccount}</div>
                          )}
                        </div>
                      </Field>
                    )}
                  </>
                )}
              </div>
            </FormSection>

            <FormSection title={t('invoices.detail.sections.billing')}>
              <div className="space-y-4">
                <Field label={t('invoices.detail.fields.name')}>
                  {detail.billingName ?? emDash}
                </Field>
                <Field label={t('invoices.detail.fields.taxNumber')}>
                  {detail.billingTaxNumber}
                </Field>
                <Field label={t('invoices.detail.fields.euTaxNumber')}>
                  {detail.billingEuTaxNumber}
                </Field>
                {hasAddressParts(
                  detail.billingZipCode,
                  detail.billingCity,
                  detail.billingAddress,
                  detail.billingCountryCode,
                ) && (
                  <Field label={t('invoices.detail.fields.address')}>
                    <AddressBlock
                      zip={detail.billingZipCode}
                      city={detail.billingCity}
                      address={detail.billingAddress}
                      country={detail.billingCountryCode}
                    />
                  </Field>
                )}
                {detail.billingEmails.length > 0 && (
                  <Field label={t('invoices.detail.fields.emails')}>
                    <ul className="space-y-0.5">
                      {detail.billingEmails.map((email) => (
                        <li key={email} className="truncate">
                          {email}
                        </li>
                      ))}
                    </ul>
                  </Field>
                )}
              </div>
            </FormSection>
          </div>

          {/* Tétel-táblázat (vízszintesen görgethető konténerben) */}
          <FormSection title={t('invoices.detail.sections.items')}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] border-collapse text-sm">
                <thead>
                  <tr className="border-border/60 text-muted-foreground border-b text-left">
                    <th className="py-2 pr-3 font-medium">{t('invoices.item.name')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('invoices.item.quantity')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('invoices.item.unitPrice')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('invoices.item.vatRate')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">{t('invoices.item.net')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('invoices.item.vatAmount')}
                    </th>
                    <th className="py-2 pl-3 text-right font-medium">{t('invoices.item.gross')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.items.map((item) => (
                    <tr key={item.id} className="border-border/40 border-b align-top last:border-0">
                      <td className="py-2.5 pr-3">
                        <div className="space-y-0.5">
                          <div>{item.name}</div>
                          {item.note && (
                            <div className="text-muted-foreground whitespace-pre-line text-xs">
                              {item.note}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                        {item.quantity}{' '}
                        <span className="text-muted-foreground">
                          {item.quantityUnit
                            ? t(`invoices.quantityUnitLabel.${item.quantityUnit}`, {
                                defaultValue: item.quantityUnit,
                              })
                            : ''}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                        {formatMoney(item.unitPrice, detail.currency, locale)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right">
                        {t(`invoices.vatRateLabel.${item.vatRate}`, { defaultValue: item.vatRate })}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                        {formatMoney(item.netPrice, detail.currency, locale)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                        {formatMoney(item.vatAmount, detail.currency, locale)}
                      </td>
                      <td className="whitespace-nowrap py-2.5 pl-3 text-right tabular-nums">
                        {formatMoney(item.grossPrice, detail.currency, locale)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FormSection>

          {/* Összesítő */}
          <FormSection title={t('invoices.detail.sections.summary')}>
            <dl className="ml-auto max-w-xs space-y-1.5 text-sm tabular-nums">
              <div className="flex items-center justify-between gap-6">
                <dt className="text-muted-foreground">{t('invoices.totals.net')}</dt>
                <dd>{formatMoney(detail.netTotal, detail.currency, locale)}</dd>
              </div>
              <div className="flex items-center justify-between gap-6">
                <dt className="text-muted-foreground">{t('invoices.totals.vat')}</dt>
                <dd>{formatMoney(detail.vatTotal, detail.currency, locale)}</dd>
              </div>
              <Divider spacing="none" inset={false} className="my-1.5" />
              <div className="flex items-center justify-between gap-6 font-semibold">
                <dt>{t('invoices.totals.gross')}</dt>
                <dd>{formatMoney(detail.grossTotal, detail.currency, locale)}</dd>
              </div>
            </dl>
          </FormSection>

          {/* Megjegyzés (egy mező — a fejléc/lábléc kettős összevonva) */}
          {hasNote && (
            <FormSection title={t('invoices.detail.sections.note')}>
              <Field label={t('invoices.detail.fields.note')}>
                <p className="whitespace-pre-line">{detail.note}</p>
              </Field>
            </FormSection>
          )}

          {/* Érintett fiókok — feloldott nevek (refs), vagy fallback darabszám (view-only) */}
          {billedIds.length > 0 && (
            <FormSection title={t('invoices.detail.sections.accounts')}>
              {resolvedAccountNames.length > 0 ? (
                <ul className="space-y-1 text-sm">
                  {resolvedAccountNames.map((name, i) => (
                    <li key={`${name}-${i}`} className="truncate">
                      {name}
                    </li>
                  ))}
                </ul>
              ) : (
                <Text variant="body" as="p" className="text-muted-foreground">
                  {t('invoices.detail.accountsCount', { count: billedIds.length })}
                </Text>
              )}
            </FormSection>
          )}
        </div>

        {/* Kiállítási életciklus dialógusok (kiállítás / sztornó / díjbekérő-érvénytelenítés). */}
        <InvoiceActionDialogs controller={actions} t={t} />
      </FormPage>
    </AppShell>
  );
}
